import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, Copy, ArrowSquareOut, ArrowClockwise } from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "@/components/ui/dialog";
import { generateLandingPage, errText } from "@/lib/api";
import { buildLandingHtml } from "@/lib/landingHtml";
import { copyToClipboard } from "@/lib/csv";

const LoadingState = ({ label }) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
        <div className="h-10 w-10 border-2 border-ink border-t-transparent rounded-full animate-spin" />
        <p className="mono-label text-neutral-600">{label}</p>
    </div>
);

export const LandingPageDialog = ({ result }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    const startGeneration = async () => {
        if (!result?.id) {
            toast.error("Search must be saved before generating a landing page");
            return;
        }
        setLoading(true);
        setError("");
        setData(null);
        try {
            const resp = await generateLandingPage(result.id);
            setData(resp);
        } catch (e) {
            const msg = errText(e, "Generation failed");
            setError(String(msg));
            toast.error(String(msg));
        } finally {
            setLoading(false);
        }
    };

    const html = data ? buildLandingHtml(data) : "";

    const handleCopyHtml = async () => {
        if (!html) return;
        const ok = await copyToClipboard(html);
        if (ok) toast.success("Full HTML copied to clipboard");
        else toast.error("Could not copy HTML — try downloading instead");
    };

    const handleOpenInNewTab = () => {
        if (!html) return;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        // Revoke after the tab has had a chance to load.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (o && !data && !loading) startGeneration();
            }}
        >
            <DialogTrigger asChild>
                <button
                    data-testid="generate-landing-btn"
                    className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    title="AI-generate a mobile-optimized landing page from this search"
                >
                    <Sparkle size={13} weight="bold" />
                    Landing Page
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="landing-page-dialog"
                className="max-w-6xl w-[95vw] rounded-none border-2 border-ink p-0 bg-white flex flex-col max-h-[92vh]"
            >
                <DialogHeader className="p-5 border-b-2 border-ink shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <DialogTitle className="font-display text-2xl font-black tracking-tighter text-ink">
                                Landing Page Preview
                            </DialogTitle>
                            <DialogDescription className="mono-label mt-1 truncate">
                                {result?.business_type} · {result?.city} · Nano-Banana hero · Tailwind mobile-optimized
                            </DialogDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                data-testid="landing-regenerate-btn"
                                onClick={startGeneration}
                                disabled={loading}
                                className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                                title="Regenerate copy and image"
                            >
                                <ArrowClockwise size={13} weight="bold" />
                                Regenerate
                            </button>
                            <button
                                data-testid="landing-open-tab-btn"
                                onClick={handleOpenInNewTab}
                                disabled={!data}
                                className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                                title="Open the rendered landing page in a new browser tab"
                            >
                                <ArrowSquareOut size={13} weight="bold" />
                                Open in Tab
                            </button>
                            <button
                                data-testid="landing-copy-html-btn"
                                onClick={handleCopyHtml}
                                disabled={!data}
                                className="brutal-btn bg-klein border border-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                                title="Copy the full standalone HTML to clipboard"
                            >
                                <Copy size={13} weight="bold" />
                                Copy HTML
                            </button>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 min-h-0 bg-neutral-100">
                    {loading && (
                        <LoadingState label="Writing copy · generating hero image · ~30s" />
                    )}
                    {!loading && error && (
                        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 px-8">
                            <p className="font-mono text-sm text-red-600 text-center max-w-md">{error}</p>
                            <button
                                onClick={startGeneration}
                                className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <ArrowClockwise size={13} weight="bold" />
                                Try Again
                            </button>
                        </div>
                    )}
                    {!loading && !error && data && (
                        <iframe
                            data-testid="landing-preview-iframe"
                            title="Landing page preview"
                            srcDoc={html}
                            className="w-full h-[75vh] bg-white border-0"
                            sandbox="allow-scripts"
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
